import type { AudioTransport, RoomMode, VideoTransport } from "./roomControl";

import {
  normalizeDeviceChoice,
  NO_DEVICE_CHOICES,
  type DeviceChoice,
} from "./deviceChoiceStore";
import type { CalibrationStore } from "./voiceCalibrationStore";

/**
 * Everything a visitor has settled, split by who owns it.
 *
 * Three owners, and the split is the point: **a setting's owner is a fact of
 * the type it is declared in**, not of a list kept somewhere else. What crosses
 * to another machine is derived from `PersonSettings` below, so a setting that
 * describes this machine cannot be made to travel without first being moved
 * between the two interfaces — which is a type change, and visible in a diff.
 * The list this replaced could be edited without either.
 *
 * The room's own settings are not here at all. They belong to the room, they
 * are published by its host, and they are kept in the room's row —
 * `roomConfig.ts` reads them and `roomControl.ts` is what a host changes.
 *
 * It lives here rather than beside the hook because `src/engine/` may not import
 * from `src/hooks/` — the store is state, and the hook is the React seam over
 * it. `src/engine/media/localMedia.ts` reads the mic settings on a code path with
 * no component in sight, which is what made the old placement a layering
 * violation rather than a matter of taste.
 *
 * Settings belong to the **Room Access** context; see the context map in
 * `eventcatalog/domains/syncani/index.mdx`.
 */

/**
 * What describes this machine, and would be wrong on another.
 *
 * Both are measurements of hardware rather than preferences about it: what a
 * microphone was found to be, not what the visitor wants done with it. That is
 * the test for admitting anything here — a value that would still be true if
 * the visitor changed their mind is a fact about the device.
 */
export interface DeviceSettings {
  /**
   * The microphone, speaker and camera this visitor picked, stored so they are
   * not picked again on every visit (`media/deviceChoice.ts` says how one is
   * matched back to the hardware). Null until they pick, which is when the
   * system's own default decides and keeps deciding.
   *
   * Device-owned rather than the person's, and deliberately: an identifier a
   * laptop's browser issued for a headset names nothing on a phone, and pushing
   * one there would break working audio on a machine the visitor never
   * configured.
   */
  audioInput: DeviceChoice | null;
  camera: DeviceChoice | null;
  /**
   * What each microphone was last measured at, keyed by the device
   * (`src/engine/media/voiceCalibration.ts`).
   *
   * Kept so a visitor is levelled on their first sentence rather than after it:
   * loudness is a fact about a microphone and a voice, not about a room, and
   * neither changes between two visits.
   */
  voiceCalibration: CalibrationStore;
  /**
   * Whether the room's controls show their keyboard shortcuts (#797). Off
   * unless asked, and the machine's rather than the person's: shortcuts are
   * the keyboard's, and a phone has none.
   */
  shortcutHints: boolean;
}

/** What belongs to the visitor, and means the same wherever they sign in. */
export interface PersonSettings {
  /** Ambient campfire crackle sound. */
  campfireSound: boolean;
  /** Distance-based volume and stereo panning for remote voices. */
  spatialAudio: boolean;
  /** Float the room in a Picture-in-Picture window while screen sharing. */
  autoPipOnShare: boolean;
  /** What this visitor calls their narrator; kept so the panel does not forget it. */
  narratorName: string;
  /** The provider voice this visitor last narrated with; empty until they pick one. */
  narratorVoiceId: string;
  /** ElevenLabs delivery controls, kept per visitor with the chosen voice. */
  narratorVoiceSettings: NarratorVoiceSettings;
  /**
   * Whether this visitor's camera publishes a head crop, and whether they see
   * one drawn over a sender who published a whole frame. Theirs throughout — no
   * host broadcast overrides it.
   */
  headZoom: boolean;
  /**
   * The kind of room this visitor opens, and so how it sounds. Only a starting
   * point, like the three below: in a room the host's broadcast decides, and
   * this is what the host publishes once, on opening one.
   */
  roomMode: RoomMode;
  /** The video path this visitor opens their own rooms with, chosen the same way. */
  videoTransport: VideoTransport;
  /** The audio path this visitor opens their own rooms with, chosen the same way. */
  audioTransport: AudioTransport;
  /**
   * The language this visitor reads the interface in. Empty means they have
   * never said, and the browser's own order of preference decides — see
   * `state/locale.ts`, which is the only module that should read this key.
   */
  locale: string;
  /**
   * Whether this visitor has been told how a room's mode works, in the hint
   * their first room shows over its stage. The person's, not the machine's: a
   * visitor who dismissed it on one computer has learnt it, and being told
   * again on another would be the room forgetting who they are.
   */
  firstRoomSeen: boolean;
}

/** Both groups as one record, which is how the store holds them. */
export type Settings = DeviceSettings & PersonSettings;

export interface NarratorVoiceSettings {
  speed: number;
  stability: number;
  style: number;
  similarityBoost: number;
  speakerBoost: boolean;
}

export const DEFAULT_NARRATOR_VOICE_SETTINGS: NarratorVoiceSettings = {
  speed: 1,
  stability: 0.38,
  style: 0.55,
  similarityBoost: 0.75,
  speakerBoost: true,
};

const DEVICE_DEFAULTS: DeviceSettings = {
  ...NO_DEVICE_CHOICES,
  voiceCalibration: {},
  shortcutHints: false,
};

const PERSON_DEFAULTS: PersonSettings = {
  campfireSound: true,
  spatialAudio: true,
  autoPipOnShare: true,
  narratorName: "Narrator",
  narratorVoiceId: "",
  narratorVoiceSettings: DEFAULT_NARRATOR_VOICE_SETTINGS,
  headZoom: true,
  // The room a visitor opens before they have chosen one. Plain is the one
  // nobody has to be taught: every other product has already taught it, so an
  // account that presses the way in without reading the four gets a room that
  // behaves the way they expect. Campfire is what the product is for and is
  // one press away, which is a better place for it than in front of somebody
  // who has not decided yet.
  roomMode: "plain",
  videoTransport: "mesh",
  audioTransport: "mesh",
  locale: "",
  firstRoomSeen: false,
};

export const DEFAULT_SETTINGS: Settings = {
  ...DEVICE_DEFAULTS,
  ...PERSON_DEFAULTS,
};

/**
 * The settings an account carries between devices: the person's group, read off
 * its own defaults.
 *
 * Derived rather than listed, which is the whole of the guarantee. A key added
 * to `PersonSettings` starts travelling because it is the person's; one added
 * to `DeviceSettings` cannot be made to travel by editing anything here.
 */
const isPersonSettingKey = (key: string): key is keyof PersonSettings =>
  key in PERSON_DEFAULTS;

export const PERSON_SETTING_KEYS: readonly (keyof PersonSettings)[] =
  Object.keys(PERSON_DEFAULTS).filter(isPersonSettingKey);

const isDeviceSettingKey = (key: string): key is keyof DeviceSettings =>
  key in DEVICE_DEFAULTS;

/** The settings that stay on this machine, read off their own defaults. */
export const DEVICE_SETTING_KEYS: readonly (keyof DeviceSettings)[] =
  Object.keys(DEVICE_DEFAULTS).filter(isDeviceSettingKey);

/**
 * The key keeps the name it had when this held one undivided record. Renaming
 * it would orphan every visitor's stored settings to no purpose: the split is
 * in the types, and the disk sees the same object it always did.
 */
const STORAGE_KEY = "room-preferences";

/**
 * The kind of room a setting from before the modes existed means, if it means
 * one. Both spatial acoustics are carried, not only the one Bubbles grew out
 * of: Campfire used to be what an unset mode fell back to, so leaving it to the
 * default would move an old visitor to Plain the day the default changed.
 */
function modeFromAcoustics(acoustics: unknown): RoomMode | undefined {
  if (acoustics === "proximity") return "bubbles";
  return acoustics === "campfire" ? acoustics : undefined;
}

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed: Partial<Settings> & {
      faceCrop?: unknown;
      acoustics?: unknown;
      micProcessing?: unknown;
      micLevelling?: unknown;
    } = JSON.parse(raw);
    // The microphone chain has nothing left to choose and nothing left to
    // remember (`clean-the-voice-with-ai-coustics`), so its two records are
    // dropped here rather than carried forward as dead weight.
    const {
      faceCrop: _faceCrop,
      acoustics,
      micProcessing: _micProcessing,
      micLevelling: _micLevelling,
      ...current
    } = parsed;
    if (typeof current.headZoom !== "boolean") delete current.headZoom;
    // The mode absorbed the acoustics, so somebody who had chosen one goes on
    // opening that kind of room. Only while they have never picked a mode of
    // their own: a mode they chose knowing what it was is the later statement.
    current.roomMode ??= modeFromAcoustics(acoustics);
    return {
      ...DEFAULT_SETTINGS,
      ...current,
      voiceCalibration:
        typeof parsed.voiceCalibration === "object" &&
        parsed.voiceCalibration !== null
          ? parsed.voiceCalibration
          : {},
      narratorVoiceSettings: {
        ...DEFAULT_NARRATOR_VOICE_SETTINGS,
        ...(parsed.narratorVoiceSettings ?? {}),
      },
      audioInput: normalizeDeviceChoice(parsed.audioInput),
      camera: normalizeDeviceChoice(parsed.camera),
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

let state: Settings = typeof window === "undefined" ? DEFAULT_SETTINGS : load();
const listeners = new Set<() => void>();

/** Subscribe to changes. The hook's `useSyncExternalStore` source. */
export function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current snapshot, by reference, so React can compare it cheaply. */
export function getSnapshot(): Settings {
  return state;
}

/** Read the current settings outside of React. */
export function getSettings(): Settings {
  return state;
}

export function setSetting<K extends keyof Settings>(
  key: K,
  value: Settings[K],
) {
  if (state[key] === value) return;
  state = { ...state, [key]: value };
  persist();
  listeners.forEach((l) => l());
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable: keep in-memory only */
  }
}

export function setNarratorVoiceSettings(
  patch: Partial<NarratorVoiceSettings>,
) {
  const next = { ...state.narratorVoiceSettings, ...patch };
  setSetting("narratorVoiceSettings", next);
}

/**
 * Applies a whole set at once — the account's copy, restored after signing in.
 * One notification instead of one per key, and unknown or unchanged fields
 * leave the store alone.
 */
export function applySettings(patch: Partial<Settings>) {
  const next: Settings = { ...state };
  let changed = false;
  Object.keys(patch).forEach((key) => {
    const value = Reflect.get(patch, key);
    if (value === undefined) return;
    if (JSON.stringify(Reflect.get(state, key)) === JSON.stringify(value))
      return;
    Object.assign(next, { [key]: value });
    changed = true;
  });
  if (!changed) return;
  state = next;
  persist();
  listeners.forEach((l) => l());
}
